'use client'

import { useState, useEffect } from 'react'
import { X, ChevronLeft, ChevronRight, Check, Calendar, ArrowRight, Building2, DollarSign, MapPin, User, Users, Mail, Phone, Globe, Home, Key, Tag, TrendingUp } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface TypeformConsultationModalProps {
  isOpen: boolean
  onClose: () => void
  source?: string
  inline?: boolean
}

interface FormData {
  clientType: string
  propertyTypes: string[]
  purpose: string
  budgetRange: string
  paymentMode: string
  monthlyBudget: string
  leaseTerm: string
  moveInTimeline: string
  preferredLocations: string[]
  bedrooms: string
  bathrooms: string
  amenities: string[]
  timeline: string
  propertyLocation: string
  priceExpectation: string
  propertyStatus: string
  inquiryNature: string
  prcLicense: string
  name: string
  email: string
  phone: string
  currentLocation: string
  meetingFormat: string
  heardFrom: string
  additionalNotes: string
  acknowledged: boolean
}

type Question = {
  id: string
  type: 'single' | 'multiple' | 'text' | 'textarea' | 'checkbox'
  question: string
  subtext?: string
  options?: { value: string; label: string; sublabel?: string; icon?: any }[]
  field: keyof FormData
  required: boolean
  validation?: (value: any) => boolean
  errorMessage?: string
  condition?: (formData: FormData) => boolean
}

export default function TypeformConsultationModal({ isOpen, onClose, source = 'unknown', inline = false }: TypeformConsultationModalProps) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward')

  const [formData, setFormData] = useState<FormData>({
    clientType: '',
    propertyTypes: [],
    purpose: '',
    budgetRange: '',
    paymentMode: '',
    monthlyBudget: '',
    leaseTerm: '',
    moveInTimeline: '',
    preferredLocations: [],
    bedrooms: '',
    bathrooms: '',
    amenities: [],
    timeline: '',
    propertyLocation: '',
    priceExpectation: '',
    propertyStatus: '',
    inquiryNature: '',
    prcLicense: '',
    name: '',
    email: '',
    phone: '',
    currentLocation: '',
    meetingFormat: 'in-person',
    heardFrom: '',
    additionalNotes: '',
    acknowledged: false
  })

  const GOOGLE_CALENDAR_LINK = process.env.NEXT_PUBLIC_GOOGLE_CALENDAR_CONSULTATION_LINK || 'https://calendar.google.com/calendar/appointments/schedules/YOUR_SCHEDULE_ID'

  // Define all questions with conditional logic
  const allQuestions: Question[] = [
    // Q1: Client Type
    {
      id: 'client-type',
      type: 'single',
      question: 'How can we assist you today?',
      subtext: 'Select the option that best describes your needs',
      field: 'clientType',
      required: true,
      options: [
        { value: 'buyer', label: 'Buyer', sublabel: 'Purchasing a property', icon: Home },
        { value: 'tenant', label: 'Tenant', sublabel: 'Looking to rent', icon: Key },
        { value: 'landlord', label: 'Landlord', sublabel: 'Selling or leasing property', icon: Building2 },
        { value: 'investor', label: 'Investor', sublabel: 'Investment opportunities', icon: TrendingUp },
        { value: 'broker', label: 'Broker / Agent', sublabel: 'Co-brokerage', icon: Users }
      ]
    },

    // Q2: Property Types (Buyers/Investors)
    {
      id: 'property-types',
      type: 'multiple',
      question: 'What type of property are you interested in?',
      subtext: 'Select all that apply',
      field: 'propertyTypes',
      required: false,
      condition: (data) => ['buyer', 'investor'].includes(data.clientType),
      options: [
        { value: 'Condominium', label: 'Condominium' },
        { value: 'House & Lot', label: 'House & Lot' },
        { value: 'Penthouse', label: 'Penthouse' },
        { value: 'Townhouse', label: 'Townhouse' },
        { value: 'Land', label: 'Land' },
        { value: 'Commercial', label: 'Commercial' }
      ]
    },

    // Q3: Purpose (Buyers/Investors)
    {
      id: 'purpose',
      type: 'single',
      question: 'What is the purpose of this property?',
      field: 'purpose',
      required: false,
      condition: (data) => ['buyer', 'investor'].includes(data.clientType),
      options: [
        { value: 'End-use', label: 'End-use', sublabel: 'Personal residence' },
        { value: 'Investment', label: 'Investment', sublabel: 'Rental income' },
        { value: 'Both', label: 'Both', sublabel: 'Live in + future investment' }
      ]
    },

    // Q4a: Budget Range (Buyers/Investors)
    {
      id: 'budget-range',
      type: 'single',
      question: 'What is your budget range?',
      subtext: '💡 This helps us show you the most relevant properties',
      field: 'budgetRange',
      required: true,
      condition: (data) => ['buyer', 'investor'].includes(data.clientType),
      options: [
        { value: '₱5M–₱10M', label: '₱5M – ₱10M' },
        { value: '₱10M–₱15M', label: '₱10M – ₱15M' },
        { value: '₱15M–₱20M', label: '₱15M – ₱20M' },
        { value: '₱20M–₱30M', label: '₱20M – ₱30M' },
        { value: '₱30M–₱50M', label: '₱30M – ₱50M' },
        { value: '₱50M–₱80M', label: '₱50M – ₱80M' },
        { value: '₱80M–₱120M', label: '₱80M – ₱120M' },
        { value: '₱120M–₱200M', label: '₱120M – ₱200M' },
        { value: '₱200M+', label: '₱200M+' }
      ]
    },

    // Q4a2: Payment Mode (Buyers/Investors)
    {
      id: 'payment-mode',
      type: 'single',
      question: 'How do you plan to purchase?',
      subtext: '💰 This helps us understand your financing readiness',
      field: 'paymentMode',
      required: true,
      condition: (data) => ['buyer', 'investor'].includes(data.clientType),
      options: [
        { value: 'Cash', label: 'Cash', sublabel: 'Full payment upfront' },
        { value: 'Bank Financing', label: 'Bank Financing', sublabel: 'Loan approval needed' },
        { value: 'Rent to Own', label: 'Rent to Own', sublabel: 'Developer program' },
        { value: 'Installment / Terms', label: 'Installment / Terms', sublabel: 'Payment plan' },
        { value: 'Combination (Cash + Loan)', label: 'Combination', sublabel: 'Cash + Financing' }
      ]
    },

    // Q4b: Monthly Budget (Tenants)
    {
      id: 'monthly-budget',
      type: 'single',
      question: 'What is your monthly rental budget?',
      subtext: '💡 This helps us show you the most suitable options',
      field: 'monthlyBudget',
      required: true,
      condition: (data) => data.clientType === 'tenant',
      options: [
        { value: '₱30k–₱50k', label: '₱30k – ₱50k' },
        { value: '₱50k–₱80k', label: '₱50k – ₱80k' },
        { value: '₱80k–₱120k', label: '₱80k – ₱120k' },
        { value: '₱120k–₱180k', label: '₱120k – ₱180k' },
        { value: '₱180k–₱250k', label: '₱180k – ₱250k' },
        { value: '₱250k+', label: '₱250k+' }
      ]
    },

    // Q5: Lease Term (Tenants)
    {
      id: 'lease-term',
      type: 'single',
      question: 'How long would you like to lease?',
      field: 'leaseTerm',
      required: false,
      condition: (data) => data.clientType === 'tenant',
      options: [
        { value: '6 months', label: '6 months' },
        { value: '12 months', label: '12 months' },
        { value: 'Flexible', label: 'Flexible', sublabel: 'Open to discussion' }
      ]
    },

    // Q6: Preferred Locations
    {
      id: 'locations',
      type: 'multiple',
      question: 'Which locations are you interested in?',
      subtext: 'Select one or more locations, then click Continue',
      field: 'preferredLocations',
      required: false,
      condition: (data) => ['buyer', 'investor', 'tenant'].includes(data.clientType),
      options: [
        { value: 'BGC', label: 'BGC' },
        { value: 'Makati', label: 'Makati' },
        { value: 'Rockwell', label: 'Rockwell' },
        { value: 'Ortigas', label: 'Ortigas' },
        { value: 'Alabang', label: 'Alabang' },
        { value: 'Quezon City', label: 'Quezon City' },
        { value: 'Other', label: 'Other locations' }
      ]
    },

    // Q6a: Bedrooms
    {
      id: 'bedrooms',
      type: 'single',
      question: 'How many bedrooms do you need?',
      subtext: 'Select the number of bedrooms you prefer',
      field: 'bedrooms',
      required: false,
      condition: (data) => ['buyer', 'investor', 'tenant'].includes(data.clientType),
      options: [
        { value: 'Studio', label: 'Studio' },
        { value: '1 BR', label: '1 Bedroom' },
        { value: '2 BR', label: '2 Bedrooms' },
        { value: '3 BR', label: '3 Bedrooms' },
        { value: '4 BR', label: '4 Bedrooms' },
        { value: '5+ BR', label: '5+ Bedrooms' },
        { value: 'Flexible', label: 'Flexible' }
      ]
    },

    // Q6b: Bathrooms
    {
      id: 'bathrooms',
      type: 'single',
      question: 'How many bathrooms do you prefer?',
      field: 'bathrooms',
      required: false,
      condition: (data) => ['buyer', 'investor', 'tenant'].includes(data.clientType),
      options: [
        { value: '1', label: '1 Bathroom' },
        { value: '2', label: '2 Bathrooms' },
        { value: '3', label: '3 Bathrooms' },
        { value: '4+', label: '4+ Bathrooms' },
        { value: 'Flexible', label: 'Flexible' }
      ]
    },

    // Q6c: Amenities & Preferences
    {
      id: 'amenities',
      type: 'multiple',
      question: 'What amenities or features are important to you?',
      subtext: 'Select all that apply',
      field: 'amenities',
      required: false,
      condition: (data) => ['buyer', 'investor', 'tenant'].includes(data.clientType),
      options: [
        { value: 'Parking', label: 'Parking' },
        { value: 'Swimming Pool', label: 'Swimming Pool' },
        { value: 'Gym / Fitness Center', label: 'Gym / Fitness Center' },
        { value: '24/7 Security', label: '24/7 Security' },
        { value: 'Pet-Friendly', label: 'Pet-Friendly' },
        { value: 'Near Schools', label: 'Near Schools' },
        { value: 'Near Shopping', label: 'Near Shopping' },
        { value: 'Near Hospitals', label: 'Near Hospitals' },
        { value: 'Garden / Balcony', label: 'Garden / Balcony' },
        { value: 'Smart Home Features', label: 'Smart Home Features' }
      ]
    },

    // Q7a: Timeline (Buyers/Investors)
    {
      id: 'timeline',
      type: 'single',
      question: 'When are you planning to proceed?',
      subtext: 'This helps us prioritize your consultation',
      field: 'timeline',
      required: true,
      condition: (data) => ['buyer', 'investor'].includes(data.clientType),
      options: [
        { value: 'immediate', label: 'Immediate', sublabel: '0–30 days' },
        { value: '1-3-months', label: '1–3 months' },
        { value: '3-6-months', label: '3–6 months' }
      ]
    },

    // Q7b: Move-in Timeline (Tenants)
    {
      id: 'move-in-timeline',
      type: 'single',
      question: 'When do you need to move in?',
      field: 'moveInTimeline',
      required: true,
      condition: (data) => data.clientType === 'tenant',
      options: [
        { value: 'Immediate', label: 'Immediate' },
        { value: 'Within 30 days', label: 'Within 30 days' },
        { value: '1-2 months', label: '1-2 months' }
      ]
    },

    // Q8: Property Location (Sellers/Landlords)
    {
      id: 'property-location',
      type: 'text',
      question: 'Where is your property located?',
      subtext: 'e.g., BGC, Makati, Rockwell',
      field: 'propertyLocation',
      required: true,
      condition: (data) => data.clientType === 'landlord'
    },

    // Q9: Price Expectation (Sellers/Landlords)
    {
      id: 'price-expectation',
      type: 'text',
      question: 'What is your price or rental expectation?',
      subtext: 'e.g., ₱50M or ₱150,000/month',
      field: 'priceExpectation',
      required: false,
      condition: (data) => data.clientType === 'landlord'
    },

    // Q10: Inquiry Nature (Brokers)
    {
      id: 'inquiry-nature',
      type: 'single',
      question: 'What type of collaboration are you interested in?',
      field: 'inquiryNature',
      required: true,
      condition: (data) => data.clientType === 'broker',
      options: [
        { value: 'Co-brokerage', label: 'Co-brokerage' },
        { value: 'Buyer referral', label: 'Buyer referral' },
        { value: 'Listing collaboration', label: 'Listing collaboration' },
        { value: 'Developer inventory', label: 'Developer inventory' }
      ]
    },

    // Q11: Name
    {
      id: 'name',
      type: 'text',
      question: 'What is your full name?',
      field: 'name',
      required: true
    },

    // Q12: Email
    {
      id: 'email',
      type: 'text',
      question: 'What is your email address?',
      field: 'email',
      required: true,
      validation: (value) => {
        if (typeof value !== 'string') return false
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(value)
      },
      errorMessage: 'Please enter a valid email address'
    },

    // Q13: Phone
    {
      id: 'phone',
      type: 'text',
      question: 'What is your phone number?',
      subtext: 'WhatsApp preferred for faster coordination',
      field: 'phone',
      required: true,
      validation: (value) => typeof value === 'string' && value.length >= 10,
      errorMessage: 'Please enter a valid phone number'
    },

    // Q14: Current Location
    {
      id: 'current-location',
      type: 'text',
      question: 'Where are you currently located?',
      subtext: 'City or Country',
      field: 'currentLocation',
      required: false
    },

    // Q15: Meeting Format
    {
      id: 'meeting-format',
      type: 'single',
      question: 'How would you like to meet?',
      subtext: 'In-person meetings are prioritized for serious consultations',
      field: 'meetingFormat',
      required: true,
      options: [
        {
          value: 'in-person',
          label: 'In-Person Consultation',
          sublabel: 'Spire Group Office – BGC (Preferred)',
          icon: MapPin
        },
        {
          value: 'google-meet',
          label: 'Google Meet',
          sublabel: 'Video call (if in-person not practical)',
          icon: Globe
        }
      ]
    },

    // Q16: How did you hear
    {
      id: 'heard-from',
      type: 'single',
      question: 'How did you hear about Spire Group?',
      field: 'heardFrom',
      required: true,
      options: [
        { value: 'Google Search', label: 'Google Search' },
        { value: 'Referral', label: 'Referral' },
        { value: 'Developer', label: 'Developer' },
        { value: 'Social Media', label: 'Social Media' },
        { value: 'Other', label: 'Other' }
      ]
    },

    // Q17: Additional Notes
    {
      id: 'additional-notes',
      type: 'textarea',
      question: 'Anything specific you\'d like us to prepare?',
      subtext: 'Optional - e.g., specific properties, investment strategies, etc.',
      field: 'additionalNotes',
      required: false
    },

    // Q18: Acknowledgment
    {
      id: 'acknowledgment',
      type: 'checkbox',
      question: 'Final Confirmation',
      subtext: 'This consultation is handled personally by our senior advisory team',
      field: 'acknowledged',
      required: true,
      options: [
        {
          value: 'true',
          label: 'I understand this is a private consultation for serious property transactions',
          sublabel: 'We dedicate significant time and resources to each consultation'
        }
      ]
    }
  ]

  // Filter questions based on conditions
  const questions = allQuestions.filter(q => !q.condition || q.condition(formData))
  const currentQuestion = questions[currentQuestionIndex]
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100

  const handleAnswer = (value: any) => {
    const field = currentQuestion.field

    if (currentQuestion.type === 'multiple') {
      const currentValues = (formData[field] as string[]) || []
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value]
      setFormData({ ...formData, [field]: newValues })
    } else if (currentQuestion.type === 'checkbox') {
      setFormData({ ...formData, [field]: !formData[field] })
    } else {
      const updatedFormData = { ...formData, [field]: value }
      setFormData(updatedFormData)

      // Auto-advance for single-choice questions (except last question)
      if (currentQuestion.type === 'single' && currentQuestionIndex < questions.length - 1) {
        setTimeout(() => {
          setDirection('forward')
          setCurrentQuestionIndex(prev => prev + 1)
        }, 300)
      }
    }
  }

  const handleNext = (overrideValue?: any) => {
    // Validation
    if (currentQuestion.required) {
      const value = overrideValue !== undefined ? overrideValue : formData[currentQuestion.field]

      if (currentQuestion.type === 'multiple') {
        if (!value || (value as string[]).length === 0) {
          alert(`Please select at least one option`)
          return
        }
      } else if (currentQuestion.type === 'checkbox') {
        if (!value) {
          alert('Please acknowledge the statement to proceed')
          return
        }
      } else {
        if (!value || value === '') {
          alert(`${currentQuestion.errorMessage || 'This field is required'}`)
          return
        }

        // Run custom validation if exists
        if (currentQuestion.validation) {
          try {
            console.log('Validating:', currentQuestion.field, '=', value)
            const isValid = currentQuestion.validation(value)
            console.log('Validation result:', isValid)
            if (!isValid) {
              alert(currentQuestion.errorMessage || 'Please enter a valid value')
              return
            }
          } catch (e) {
            console.error('Validation error:', e, 'for value:', value)
            alert(currentQuestion.errorMessage || 'Please enter a valid value')
            return
          }
        }
      }
    }

    setDirection('forward')
    setCurrentQuestionIndex(prev => Math.min(prev + 1, questions.length - 1))
  }

  const handleBack = () => {
    setDirection('backward')
    setCurrentQuestionIndex(prev => Math.max(prev - 1, 0))
  }

  const handleSubmit = async () => {
    if (!formData.acknowledged) {
      alert('Please acknowledge the final confirmation')
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('/api/inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propertyId: null,
          propertyTitle: 'Luxury Consultation Request (Typeform)',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          lookingFor: formData.clientType === 'broker' ? 'broker' : 'myself',
          preferredContact: 'Scheduled Consultation',
          inquiryType: 'Luxury Consultation',
          message: generateInquiryMessage(),
          source: source,
          metadata: {
            clientType: formData.clientType,
            budgetRange: formData.budgetRange || formData.monthlyBudget,
            timeline: formData.timeline || formData.moveInTimeline,
            locations: formData.preferredLocations,
            bedrooms: formData.bedrooms,
            bathrooms: formData.bathrooms,
            amenities: formData.amenities,
            meetingFormat: formData.meetingFormat,
            heardFrom: formData.heardFrom,
            paymentMode: formData.paymentMode,
            propertyTypes: formData.propertyTypes,
            purpose: formData.purpose
          }
        })
      })

      if (response.ok) {
        setShowSuccess(true)

        setTimeout(() => {
          window.open(GOOGLE_CALENDAR_LINK, '_blank')

          setTimeout(() => {
            resetForm()
            onClose()
          }, 2000)
        }, 2000)
      } else {
        const errorData = await response.json().catch(() => ({}))
        console.error('Submission failed:', response.status, errorData)
        throw new Error(`Submission failed: ${response.status}`)
      }
    } catch (error) {
      console.error('Error submitting consultation:', error)
      alert('Failed to submit. Please try again or contact us at +639602535917')
    } finally {
      setIsSubmitting(false)
    }
  }

  const generateInquiryMessage = (): string => {
    let message = `LUXURY CONSULTATION REQUEST (Typeform)\n\n`
    message += `Client Type: ${formData.clientType}\n`
    message += `Meeting Format: ${formData.meetingFormat === 'in-person' ? 'In-Person (BGC Office)' : 'Google Meet'}\n\n`

    if (['buyer', 'investor'].includes(formData.clientType)) {
      if (formData.propertyTypes.length > 0) message += `Property Types: ${formData.propertyTypes.join(', ')}\n`
      if (formData.purpose) message += `Purpose: ${formData.purpose}\n`
      message += `Budget Range: ${formData.budgetRange}\n`
      message += `Payment Mode: ${formData.paymentMode}\n`
      message += `Preferred Locations: ${formData.preferredLocations.join(', ')}\n`
      if (formData.bedrooms) message += `Bedrooms: ${formData.bedrooms}\n`
      if (formData.bathrooms) message += `Bathrooms: ${formData.bathrooms}\n`
      if (formData.amenities.length > 0) message += `Amenities: ${formData.amenities.join(', ')}\n`
      message += `Timeline: ${formData.timeline}\n`
    } else if (formData.clientType === 'tenant') {
      message += `Monthly Budget: ${formData.monthlyBudget}\n`
      if (formData.leaseTerm) message += `Lease Term: ${formData.leaseTerm}\n`
      message += `Preferred Locations: ${formData.preferredLocations.join(', ')}\n`
      if (formData.bedrooms) message += `Bedrooms: ${formData.bedrooms}\n`
      if (formData.bathrooms) message += `Bathrooms: ${formData.bathrooms}\n`
      if (formData.amenities.length > 0) message += `Amenities: ${formData.amenities.join(', ')}\n`
      message += `Move-in Timeline: ${formData.moveInTimeline}\n`
    } else if (formData.clientType === 'landlord') {
      if (formData.propertyLocation) message += `Property Location: ${formData.propertyLocation}\n`
      if (formData.priceExpectation) message += `Price Expectation: ${formData.priceExpectation}\n`
    } else if (formData.clientType === 'broker') {
      message += `Inquiry Nature: ${formData.inquiryNature}\n`
      if (formData.prcLicense) message += `PRC License: ${formData.prcLicense}\n`
    }

    message += `\nContact Details:\n`
    message += `Name: ${formData.name}\n`
    message += `Email: ${formData.email}\n`
    message += `Phone: ${formData.phone}\n`
    if (formData.currentLocation) message += `Location: ${formData.currentLocation}\n`

    message += `\nHeard From: ${formData.heardFrom}\n`

    if (formData.additionalNotes) {
      message += `\nAdditional Notes:\n${formData.additionalNotes}`
    }

    return message
  }

  const resetForm = () => {
    setFormData({
      clientType: '',
      propertyTypes: [],
      purpose: '',
      budgetRange: '',
      paymentMode: '',
      monthlyBudget: '',
      leaseTerm: '',
      moveInTimeline: '',
      preferredLocations: [],
      bedrooms: '',
      bathrooms: '',
      amenities: [],
      timeline: '',
      propertyLocation: '',
      priceExpectation: '',
      propertyStatus: '',
      inquiryNature: '',
      prcLicense: '',
      name: '',
      email: '',
      phone: '',
      currentLocation: '',
      meetingFormat: 'in-person',
      heardFrom: '',
      additionalNotes: '',
      acknowledged: false
    })
    setCurrentQuestionIndex(0)
    setShowSuccess(false)
  }

  if (!isOpen) return null

  return (
    <div className={inline ? "min-h-screen bg-white" : "fixed inset-0 z-[100] bg-white"}>
      {showSuccess ? (
        // Success Screen
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Perfect!</h2>
            <p className="text-lg text-gray-700 mb-2">
              Your consultation request has been received.
            </p>
            <p className="text-gray-600 mb-6">
              Redirecting you to schedule your preferred time...
            </p>
            <div className="flex items-center justify-center gap-2 text-[#d60500]">
              <Calendar className="w-5 h-5 animate-pulse" />
              <span className="font-medium">Opening calendar...</span>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Header with Progress */}
          <div className={inline ? "sticky top-0 bg-white border-b border-gray-200 z-10" : "fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-10"}>
            <div className="h-1 bg-gray-100">
              <div
                className="h-full bg-[#d60500] transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>

            <div className="flex items-center justify-between p-4 md:px-8">
              <div className="flex items-center gap-3">
                {!inline && (
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    aria-label="Close consultation"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                )}
                {!inline && (
                  <span className="text-sm font-medium text-gray-600">
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </span>
                )}
              </div>

              {!inline && (
                <div className="flex items-center gap-2">
                  <span className="text-lg md:text-xl font-bold text-gray-900 tracking-tight">
                    SPIRE
                  </span>
                  <span className="text-lg md:text-xl font-light text-gray-500">
                    GROUP
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Question Content */}
          <div className={inline ? "min-h-screen pt-12 pb-24 px-4 md:px-8 flex items-center justify-center" : "min-h-screen pt-24 pb-32 px-4 md:px-8 flex items-center justify-center"}>
            <div className="w-full max-w-2xl">
              <div
                key={currentQuestion.id}
                className={`transition-all duration-300 ${
                  direction === 'forward' ? 'animate-slide-in-right' : 'animate-slide-in-left'
                }`}
              >
                {/* Question Text */}
                <div className="mb-6">
                  <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2 leading-tight">
                    {currentQuestion.question}
                  </h2>
                  {currentQuestion.subtext && (
                    <p className="text-base md:text-lg text-gray-600">
                      {currentQuestion.subtext}
                    </p>
                  )}
                </div>

                {/* Answer Options */}
                <div className="space-y-3">
                  {/* Single/Multiple Choice */}
                  {(currentQuestion.type === 'single' || currentQuestion.type === 'multiple') && currentQuestion.options && (
                    <div className="space-y-3">
                      {currentQuestion.options.map((option) => {
                        const Icon = option.icon
                        const isSelected = currentQuestion.type === 'single'
                          ? formData[currentQuestion.field] === option.value
                          : (formData[currentQuestion.field] as string[])?.includes(option.value)

                        return (
                          <button
                            key={option.value}
                            onClick={() => handleAnswer(option.value)}
                            className={`w-full text-left p-4 md:p-6 rounded-xl border-2 transition-all hover:scale-[1.02] active:scale-98 ${
                              isSelected
                                ? 'border-[#d60500] bg-red-50'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-start gap-3 flex-1">
                                {Icon && (
                                  <div className={`flex-shrink-0 mt-1 ${isSelected ? 'text-[#d60500]' : 'text-gray-400'}`}>
                                    <Icon className="w-6 h-6" />
                                  </div>
                                )}
                                <div className="flex-1">
                                  <p className={`text-lg md:text-xl font-semibold ${isSelected ? 'text-[#d60500]' : 'text-gray-900'}`}>
                                    {option.label}
                                  </p>
                                  {option.sublabel && (
                                    <p className="text-sm md:text-base text-gray-600 mt-1">
                                      {option.sublabel}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {isSelected && (
                                <Check className="w-6 h-6 text-[#d60500] flex-shrink-0" />
                              )}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  )}

                  {/* Text Input */}
                  {currentQuestion.type === 'text' && (
                    <div>
                      <input
                        type={currentQuestion.field === 'email' ? 'email' : currentQuestion.field === 'phone' ? 'tel' : 'text'}
                        value={formData[currentQuestion.field] as string}
                        onChange={(e) => setFormData({ ...formData, [currentQuestion.field]: e.target.value })}
                        placeholder="Type your answer here..."
                        className="w-full text-lg md:text-xl p-4 md:p-6 border-2 border-gray-300 rounded-xl focus:border-[#d60500] focus:outline-none transition-colors"
                        autoFocus
                      />
                    </div>
                  )}

                  {/* Textarea */}
                  {currentQuestion.type === 'textarea' && (
                    <div>
                      <textarea
                        value={formData[currentQuestion.field] as string}
                        onChange={(e) => setFormData({ ...formData, [currentQuestion.field]: e.target.value })}
                        placeholder="Type your answer here..."
                        rows={4}
                        className="w-full text-lg md:text-xl p-4 md:p-6 border-2 border-gray-300 rounded-xl focus:border-[#d60500] focus:outline-none transition-colors resize-none"
                        autoFocus
                      />
                    </div>
                  )}

                  {/* Checkbox */}
                  {currentQuestion.type === 'checkbox' && currentQuestion.options && (
                    <div className="space-y-3">
                      {currentQuestion.options.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleAnswer(option.value)}
                          className={`w-full text-left p-4 md:p-6 rounded-xl border-2 transition-all ${
                            formData[currentQuestion.field]
                              ? 'border-[#d60500] bg-red-50'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                              formData[currentQuestion.field]
                                ? 'border-[#d60500] bg-[#d60500]'
                                : 'border-gray-300'
                            }`}>
                              {formData[currentQuestion.field] && (
                                <Check className="w-4 h-4 text-white" />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="text-base md:text-lg font-medium text-gray-900">
                                {option.label}
                              </p>
                              {option.sublabel && (
                                <p className="text-sm text-gray-600 mt-1">
                                  {option.sublabel}
                                </p>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Navigation */}
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
            <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
              {/* Back Button */}
              {currentQuestionIndex > 0 ? (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 px-4 py-3 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span className="font-medium">Back</span>
                </button>
              ) : (
                <div className="text-xs text-gray-400 hidden md:block">
                  © {new Date().getFullYear()} Spire Group
                </div>
              )}

              {/* Next/Submit Button */}
              {currentQuestionIndex === questions.length - 1 ? (
                <Button
                  onClick={() => handleSubmit()}
                  disabled={isSubmitting}
                  className="bg-[#d60500] hover:bg-[#b50400] text-white px-8 py-6 text-lg font-semibold rounded-xl disabled:opacity-50"
                >
                  {isSubmitting ? (
                    'Submitting...'
                  ) : (
                    <>
                      Book Consultation
                      <Calendar className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              ) : (
                currentQuestion.type !== 'single' && (
                  <Button
                    onClick={() => handleNext()}
                    className="bg-[#d60500] hover:bg-[#b50400] text-white px-8 py-6 text-lg font-semibold rounded-xl flex items-center gap-2"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                )
              )}
            </div>
          </div>
        </>
      )}

      <style jsx global>{`
        @keyframes slide-in-right {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slide-in-left {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }

        .animate-slide-in-left {
          animation: slide-in-left 0.3s ease-out;
        }

        .active\:scale-98:active {
          transform: scale(0.98);
        }
      `}</style>
    </div>
  )
}
